import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { getFilesByStudyId, downloadFile } from "../../../services/fileService";
import LocalizedDataGrid from "../../ui/datagrid/LocalizedDataGrid";
import { t } from "i18next";

export const ScreenExports = () => {
  const location = useLocation();
  const studyId = location.state?.studyId;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchFiles = async () => {
    setLoading(true);
    if (!studyId) {
      setErrorMessage("Study ID is missing.");
      setLoading(false);
      return;
    }
    try {
      const response = await getFilesByStudyId(studyId);

      if (response.error) {
        setErrorMessage(response.error);
        setFiles([]);
      } else if (!response.files || response.files.length === 0) {
        setErrorMessage("No files available for this study.");
        setFiles([]);
      } else {
        const formattedFiles = response.files
          .map((file, index) => ({
            id: index,
            name: file.name,
            lastModified: file.lastModified,
          }))
          .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));

        setFiles(formattedFiles);
        setErrorMessage("");
      }
    } catch (error) {
      console.error("Unexpected error in fetchFiles:", error);
      setErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileName) => {
    try {
      await downloadFile(studyId, fileName);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [studyId]);

  const columns = [
    {
      field: "name",
      headerName: t("fileName"),
      flex: 0.8,
    },
    {
      field: "lastModified",
      headerName: t("lastModified"),
      flex: 0.2,
      valueGetter: (params) =>
        new Date(params.row.lastModified).toLocaleDateString(),
    },
    {
      field: "actions",
      flex: 0.2,
      headerName: "",
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          style={{ padding: "4px 16px", marginLeft: "auto" }}
          color="primary"
          onClick={() => handleDownload(params.row.name)}
        >
          {t("download")}
        </Button>
      ),
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "80vh",
            padding: 4,
          }}
        >
          <Box sx={{ height: "100%", width: "100%" }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>
                  {t("loadingFiles")}
                </Typography>
              </Box>
            ) : errorMessage ? (
              <Typography
                color="error"
                variant="body1"
                sx={{ mt: 2, textAlign: "center" }}
              >
                {errorMessage}
              </Typography>
            ) : (
              <LocalizedDataGrid
                rows={files}
                columns={columns}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
              />
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};
